'use client';

import { c as _c } from "react/compiler-runtime";
import type { CardAISessionPhase, CardAISessionSummary, RunnerActivity, ScreenHolderView, SecureStoreStatus } from '@shared/all/domains/electron';
import type { ActiveActivity, ActiveSession, RunnerUpdateState } from '@shared/all/helpers/runningJobs';
import { makeChunkReassembler } from '@shared/all/helpers/wireChunk';
import type { DataChannelInterface } from '@shared/utils/helpers/remoteBoardAgent';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import type { RunnerLinkRoute } from '@shared/utils/helpers/runnerTransport';
import { useIceServers } from '@shared/utils/hooks/useIceServers';
import { useOnlineRunners } from '@shared/utils/hooks/useOnlineRunners';
import useUser from '@shared/utils/hooks/useUser';
import socket from '@shared/utils/socket/socket';
import { useQuery } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { client } from '../utils/trpc';
import { type RunnerConnectionState, type RunnerLinkDiagnostics, useRunnerConnection } from './useRunnerConnection';

// Files this side's view of a settled handshake against the id the server and
// the runner log under, so one failure reads as one trace. Module scope on
// purpose: useRunnerConnection lists it as a dependency, and a new function
// each render would tear the peer down and redial forever.
function reportLink(outcome: 'connected' | 'failed', linkId: string, runnerSocketId: string, detail: string, route: RunnerLinkRoute): void {
  // The wrapped client returns a Result tuple rather than throwing, and the
  // link has already settled either way, so the outcome here is ignored.
  void client.runners.linkReport.mutate({
    linkId,
    runnerSocketId,
    outcome,
    source: 'web',
    detail,
    transport: route.transport,
    pair: route.pair
  },
  // Never toast. This is a background diagnostic, and the app ships ahead of
  // the server on an OTA update, so an older server answers "no procedure
  // found on path" — which is not something to interrupt the user with.
  {
    context: {
      silenceErrors: true
    }
  });
}
export type OnlineRunner = {
  socketId: string;
  runnerId: number;
  name: string;
  platform: string;
  // True for a runner you don't own but can use via a board it's shared to.
  shared?: boolean;
};

// One channel per online runner — opened at app root so leaving a space
// doesn't tear down its agent. Every space's RunnerProvider reads the
// channel for its preferred runner out of here instead of owning one.
//
// Active sessions are also aggregated here: each channel emits
// `session-update` events tagged with their `spaceSlug` on the wire, so we
// can build a cross-space list without ever instantiating a per-space agent
// just for the pill.

// Shared with mobile so both panels aggregate the same shape.
export type { ActiveActivity, ActiveSession, RunnerUpdateState };

/** What one runner has reported about itself over its channel. */
type RunnerReport = {
  version?: string;
  secure?: SecureStoreStatus;
  /** Who has the runner's video stream, as it last said. */
  screenHolder?: ScreenHolderView | null;
};
type RunnersContextValue = {
  runners: OnlineRunner[];
  channels: Record<string, DataChannelInterface>;
  /** Simulator video per runner, when one is flowing. Split out beside
   *  `channels` for the same reason: a stream and a channel arrive at
   *  different moments and a consumer wants whichever it has. */
  streams: Record<string, MediaStream>;
  // socketId → live handshake state, straight from useRunnerConnection. Kept
  // beside `channels` because a channel only says "connected"; a failed or
  // timed-out handshake has no channel and would otherwise be indistinguishable
  // from one that is still dialling.
  states: Record<string, RunnerConnectionState>;
  // socketId → what the handshake did, so a failed link can say why.
  diagnostics: Record<string, RunnerLinkDiagnostics>;
  // socketId → dial this runner again. A failed handshake is otherwise
  // permanent for the life of the page: nothing the effect depends on changes
  // when the user re-picks the same runner.
  retries: Record<string, () => void>;
  sessions: ActiveSession[];
  activities: ActiveActivity[];
  // runnerId → version the runner reported over its channel.
  versions: Record<number, string>;
  // runnerId → how the runner's connection storage is doing, as it told its
  // OWNER on channel open and after every unlock. Absent for a runner shared
  // to you, and for one too old to announce it.
  secure: Record<number, SecureStoreStatus>;
  // runnerId → who has the runner's one video stream (the simulator or the
  // desktop view), as the runner told this link. Absent or null = nobody.
  screenHolders: Record<number, ScreenHolderView | null>;
  // Latest published runner version from npm (server-cached); null until loaded.
  latestVersion: string | null;
  // runnerId → in-flight update state, set after the user approves an update.
  updateState: Record<number, RunnerUpdateState>;
  // Approve an update for the runner on this socket — sends the control message
  // the daemon acts on (install latest + restart).
  requestUpdate: (socketId: string) => void;
};

// Exported so the onboarding demo can provide a fixture runner fleet
export const RunnersContext = createContext<RunnersContextValue>({
  runners: [],
  channels: {},
  streams: {},
  states: {},
  diagnostics: {},
  retries: {},
  sessions: [],
  activities: [],
  versions: {},
  secure: {},
  screenHolders: {},
  latestVersion: null,
  updateState: {},
  requestUpdate: () => {}
});

// What one RunnerLink reports up: the channel once it opens, plus the
// handshake state that says whether a missing channel is still dialling or has
// given up.
type RunnerLinkState = {
  channel: DataChannelInterface | null;
  state: RunnerConnectionState;
  diagnostics: RunnerLinkDiagnostics;
  /** Re-dial. Owned by RunnerLink because the attempt counter lives there. */
  retry: () => void;
  /** The simulator's video track, when one is flowing on this link. */
  stream: MediaStream | null;
};
const TERMINAL_PHASES = new Set<CardAISessionPhase>(['done']);
function sessionKey(runnerId: number, spaceSlug: string, postId: string): string {
  return `${runnerId}::${spaceSlug}::${postId}`;
}
export function RunnersProvider(t0) {
  const $ = _c(55);
  const {
    children
  } = t0;
  const {
    currentUser
  } = useUser();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [runners, setRunners] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [links, setLinks] = useState(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [sessions, setSessions] = useState(t3);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const [activities, setActivities] = useState(t4);
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {};
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const [reports, setReports] = useState(t5);
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {};
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const [updateState, setUpdateState] = useState(t6);
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = ["runnerLatestVersion"];
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const t8 = runners.length > 0;
  let t9;
  if ($[7] !== t8) {
    t9 = {
      queryKey: t7,
      queryFn: _temp,
      staleTime: 3600000,
      enabled: t8
    };
    $[7] = t8;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const {
    data: latestData
  } = useQuery(t9);
  const latestVersion = latestData?.version ?? null;
  let t10;
  let t11;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      const onOnline = data => {
        setRunners(prev => {
          if (prev.some(r => r.runnerId === data.runnerId)) {
            return prev;
          }
          return [...prev, data];
        });
      };
      const onOffline = data_0 => {
        setRunners(prev_0 => prev_0.filter(r_0 => r_0.runnerId !== data_0.runnerId));
      };
      const onDisconnect = () => setRunners([]);
      socket.on("runnerOnline", onOnline);
      socket.on("runnerOffline", onOffline);
      socket.on("disconnect", onDisconnect);
      return () => {
        socket.off("runnerOnline", onOnline);
        socket.off("runnerOffline", onOffline);
        socket.off("disconnect", onDisconnect);
      };
    };
    t11 = [];
    $[9] = t10;
    $[10] = t11;
  } else {
    t10 = $[9];
    t11 = $[10];
  }
  useEffect(t10, t11);
  let t12;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = fetched => {
      setRunners(prev_1 => {
        const missing = fetched.filter(f => !prev_1.some(r_1 => r_1.runnerId === f.runnerId));
        return missing.length === 0 ? prev_1 : [...prev_1, ...missing];
      });
    };
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  useOnlineRunners(client, currentUser.isAuthenticated === true, t12);
  let t13;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = (socketId, link) => {
      setLinks(prev_2 => {
        const next = {
          ...prev_2
        };
        if (link) {
          next[socketId] = link;
        } else {
          delete next[socketId];
        }
        return next;
      });
    };
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  const registerLink = t13;
  let channels;
  let diagnostics;
  let retries;
  let states;
  let streams;
  if ($[13] !== links) {
    channels = {};
    streams = {};
    states = {};
    diagnostics = {};
    retries = {};
    for (const [socketId_0, link_0] of Object.entries(links)) {
      if (link_0.channel) {
        channels[socketId_0] = link_0.channel;
      }
      if (link_0.stream) {
        streams[socketId_0] = link_0.stream;
      }
      states[socketId_0] = link_0.state;
      diagnostics[socketId_0] = link_0.diagnostics;
      retries[socketId_0] = link_0.retry;
    }
    $[13] = links;
    $[14] = channels;
    $[15] = diagnostics;
    $[16] = retries;
    $[17] = states;
    $[18] = streams;
  } else {
    channels = $[14];
    diagnostics = $[15];
    retries = $[16];
    states = $[17];
    streams = $[18];
  }
  let t14;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = (runner, spaceSlug, summary) => {
      setSessions(prev_3 => {
        const k = sessionKey(runner.runnerId, spaceSlug, String(summary.postId));
        if (summary.phase === null || TERMINAL_PHASES.has(summary.phase)) {
          return prev_3.filter(s => sessionKey(s.runnerId, s.spaceSlug, s.postId) !== k);
        }
        const next_0 = {
          runnerId: runner.runnerId,
          runnerName: runner.name,
          spaceSlug,
          postId: String(summary.postId),
          taskTitle: summary.taskTitle || "Untitled card",
          phase: summary.phase,
          mode: summary.mode,
          startedAt: summary.startedAt || Date.now(),
          agentState: summary.agentState,
          exposed: summary.exposed,
          agentActivity: summary.agentActivity,
          git: summary.git
        };
        const idx = prev_3.findIndex(s_0 => sessionKey(s_0.runnerId, s_0.spaceSlug, s_0.postId) === k);
        if (idx === -1) {
          return [...prev_3, next_0];
        }
        const copy = prev_3.slice();
        copy[idx] = next_0;
        return copy;
      });
    };
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  const upsertSession = t14;
  let t15;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = (runner_0, spaceSlug_0, activity) => {
      setActivities(prev_4 => {
        const same = a => a.runnerId === runner_0.runnerId && a.spaceSlug === spaceSlug_0 && a.id === activity.id;
        if (activity.state === null) {
          return prev_4.filter(a_0 => !same(a_0));
        }
        const next_1 = {
          ...activity,
          runnerId: runner_0.runnerId,
          runnerName: runner_0.name,
          spaceSlug: spaceSlug_0
        };
        const idx_0 = prev_4.findIndex(same);
        if (idx_0 === -1) {
          return [...prev_4, next_1];
        }
        const copy_0 = prev_4.slice();
        copy_0[idx_0] = next_1;
        return copy_0;
      });
    };
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  const upsertActivity = t15;
  let t16;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = runnerId => {
      setSessions(prev_5 => prev_5.filter(s_1 => s_1.runnerId !== runnerId));
      setActivities(prev_6 => prev_6.filter(a_1 => a_1.runnerId !== runnerId));
    };
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  const dropRunnerSessions = t16;
  let screenHolders;
  let secure;
  let versions;
  if ($[22] !== reports) {
    versions = {};
    secure = {};
    screenHolders = {};
    for (const [id, report] of Object.entries(reports)) {
      if (report.version) {
        versions[Number(id)] = report.version;
      }
      if (report.secure) {
        secure[Number(id)] = report.secure;
      }
      if (report.screenHolder) {
        screenHolders[Number(id)] = report.screenHolder;
      }
    }
    $[22] = reports;
    $[23] = screenHolders;
    $[24] = secure;
    $[25] = versions;
  } else {
    screenHolders = $[23];
    secure = $[24];
    versions = $[25];
  }
  let t17;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = (runnerId_0, screenHolder) => {
      setReports(prev_7 => ({
        ...prev_7,
        [runnerId_0]: {
          ...prev_7[runnerId_0],
          screenHolder
        }
      }));
    };
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  const setRunnerScreenHolder = t17;
  let t18;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = (runnerId_1, status) => {
      setReports(prev_8 => ({
        ...prev_8,
        [runnerId_1]: {
          ...prev_8[runnerId_1],
          secure: status
        }
      }));
    };
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  const setRunnerSecure = t18;
  let t19;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = (runnerId_2, version) => {
      setReports(prev_9 => prev_9[runnerId_2]?.version === version ? prev_9 : {
        ...prev_9,
        [runnerId_2]: {
          ...prev_9[runnerId_2],
          version
        }
      });
      setUpdateState(prev_10 => {
        if (!(runnerId_2 in prev_10)) {
          return prev_10;
        }
        const next_2 = {
          ...prev_10
        };
        delete next_2[runnerId_2];
        return next_2;
      });
    };
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  const setRunnerVersion = t19;
  let t20;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = (runnerId_3, state) => {
      setUpdateState(prev_11 => ({
        ...prev_11,
        [runnerId_3]: state
      }));
    };
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  const setRunnerUpdateState = t20;
  let t21;
  if ($[30] !== channels || $[31] !== runners) {
    t21 = socketId_1 => {
      const channel = channels[socketId_1];
      if (!channel) {
        return;
      }
      const runner_1 = runners.find(r_2 => r_2.socketId === socketId_1);
      if (!runner_1 || runner_1.shared) {
        return;
      }
      channel.send(JSON.stringify({
        type: "runner-control",
        action: "update"
      }));
      setRunnerUpdateState(runner_1.runnerId, "installing");
    };
    $[30] = channels;
    $[31] = runners;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  const requestUpdate = t21;
  let t22;
  if ($[33] !== activities || $[34] !== channels || $[35] !== diagnostics || $[36] !== latestVersion || $[37] !== requestUpdate || $[38] !== retries || $[39] !== runners || $[40] !== screenHolders || $[41] !== secure || $[42] !== sessions || $[43] !== states || $[44] !== streams || $[45] !== updateState || $[46] !== versions) {
    t22 = {
      runners,
      channels,
      streams,
      states,
      diagnostics,
      retries,
      sessions,
      activities,
      versions,
      secure,
      screenHolders,
      latestVersion,
      updateState,
      requestUpdate
    };
    $[33] = activities;
    $[34] = channels;
    $[35] = diagnostics;
    $[36] = latestVersion;
    $[37] = requestUpdate;
    $[38] = retries;
    $[39] = runners;
    $[40] = screenHolders;
    $[41] = secure;
    $[42] = sessions;
    $[43] = states;
    $[44] = streams;
    $[45] = updateState;
    $[46] = versions;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== runners) {
    let t24;
    if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
      t24 = r_3 => <RunnerLink key={r_3.socketId} runner={r_3} onLink={l => registerLink(r_3.socketId, l)} onSessionUpdate={upsertSession} onActivityUpdate={upsertActivity} onChannelGone={dropRunnerSessions} onVersion={setRunnerVersion} onSecure={setRunnerSecure} onScreenHolder={setRunnerScreenHolder} onUpdateState={setRunnerUpdateState} />;
      $[50] = t24;
    } else {
      t24 = $[50];
    }
    t23 = runners.map(t24);
    $[48] = runners;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[51] !== children || $[52] !== t22 || $[53] !== t23) {
    t24 = <RunnersContext.Provider value={t22}>{t23}{children}</RunnersContext.Provider>;
    $[51] = children;
    $[52] = t22;
    $[53] = t23;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  return t24;
}
function _temp() {
  return unwrapFromResult(client.runners.latestVersion.query());
}
function RunnerLink({
  runner,
  onLink,
  onSessionUpdate,
  onActivityUpdate,
  onChannelGone,
  onVersion,
  onSecure,
  onScreenHolder,
  onUpdateState
}: {
  runner: OnlineRunner;
  onLink: (link: RunnerLinkState | null) => void;
  onSessionUpdate: (runner: OnlineRunner, spaceSlug: string, summary: CardAISessionSummary) => void;
  onActivityUpdate: (runner: OnlineRunner, spaceSlug: string, activity: RunnerActivity) => void;
  onChannelGone: (runnerId: number) => void;
  onVersion: (runnerId: number, version: string) => void;
  onSecure: (runnerId: number, status: SecureStoreStatus) => void;
  onScreenHolder: (runnerId: number, holder: ScreenHolderView | null) => void;
  onUpdateState: (runnerId: number, state: RunnerUpdateState) => void;
}) {
  // One credential per peer: Cloudflare allocates a relay per credential, so a
  // list shared across runners would leave all but one of them without one.
  const iceServers = useIceServers(client);
  // Counting attempts is what lets a user re-dial: every other input to the
  // handshake is identical on a retry, so without this the effect would not
  // re-run and a failed link stayed failed until the page was reloaded.
  const [attempt, setAttempt] = useState(0);
  const {
    channel,
    state,
    diagnostics,
    stream
  } = useRunnerConnection(runner.socketId, iceServers, attempt, reportLink);
  // setAttempt is stable, so the closure the effect below publishes stays
  // correct even though this is a new function every render.
  const retry = () => setAttempt(n => n + 1);
  useEffect(() => {
    onLink({
      channel,
      state,
      diagnostics,
      retry,
      stream
    });
    return () => onLink(null);
    // onLink is referentially unstable from parent state setters; we
    // intentionally only react to the connection itself changing.
  }, [channel, state, diagnostics, stream]);

  // Sniff session-update events directly off the wire (no per-space agent
  // needed). spaceSlug travels in the envelope so cross-space attribution
  // is free.
  useEffect(() => {
    if (!channel) return;
    const reassembler = makeChunkReassembler();
    const unsub = channel.onMessage(raw => {
      const wire = reassembler.feed(raw);
      if (wire === null) return;
      try {
        const msg = JSON.parse(wire) as {
          type?: string;
          spaceSlug?: string;
          event?: string;
          payload?: unknown;
          version?: string;
          state?: RunnerUpdateState;
          status?: SecureStoreStatus;
          holder?: ScreenHolderView | null;
        };
        // Runner announces its version on channel open (see daemon.ts dc.onOpen).
        if (msg.type === 'runner-info' && typeof msg.version === 'string') {
          onVersion(runner.runnerId, msg.version);
          return;
        }
        // Its connection storage state: sent to the owner on open, and to
        // everyone after an unlock.
        if (msg.type === 'runner-secure' && msg.status) {
          onSecure(runner.runnerId, msg.status);
          return;
        }
        // Who has its video stream: sent on open and on every change, to a
        // link that may watch it.
        if (msg.type === 'runner-screen-holder') {
          onScreenHolder(runner.runnerId, msg.holder ?? null);
          return;
        }
        // Progress while applying an approved self-update.
        if (msg.type === 'runner-update' && msg.state) {
          onUpdateState(runner.runnerId, msg.state);
          return;
        }
        // Event name is `board-agent:<spaceSlug>:session-update` — the
        // engine's emitter prefixes everything (see board-agent-core/session.ts).
        // We pull spaceSlug out of the envelope and just match the suffix.
        if (msg.type === 'event' && typeof msg.event === 'string' && msg.event.endsWith(':session-update') && msg.spaceSlug) {
          onSessionUpdate(runner, msg.spaceSlug, msg.payload as CardAISessionSummary);
          return;
        }
        // Same envelope, for the runner work that isn't a card session.
        if (msg.type === 'event' && typeof msg.event === 'string' && msg.event.endsWith(':activity-update') && msg.spaceSlug) {
          onActivityUpdate(runner, msg.spaceSlug, msg.payload as RunnerActivity);
        }
      } catch {
        // malformed frame — ignore
      }
    });
    return () => {
      unsub();
      // Drop this runner's sessions when its channel tears down.
      onChannelGone(runner.runnerId);
      // The next link is told again; until then nobody is known to hold it.
      onScreenHolder(runner.runnerId, null);
    };
  }, [channel]);
  return null;
}
export function useRunners() {
  return useContext(RunnersContext);
}